import { notify } from '@yembo/yemblocks-core';
import dayjs from 'dayjs';
import { queryClient } from 'src/queries';
import { postInsured } from 'src/queries/Insured';
import {
  JOB_QUERY_KEY,
  ENTITY_KEY,
  EVENTS_QUERY_KEY,
  LIST_KEY,
  ROOM_OPTION_LIST_QUERY_KEY,
  ROOM_QUERY_KEY,
} from 'src/queries/queryKeys';
import type { Employee, Job, SurveyType } from 'src/types';
import { initializeRooms } from '../../queries/Room';
import { withShortPoll } from '../_util/withShortPoll';
import { yemboApiCall } from './_yemboApiCall';

// biome-ignore lint/suspicious/noExplicitAny: legacy API action interface
type ApiAction = Record<string, any>;

const url = 'job';

const placeTypes = ['origin', 'stop', 'destination'];

const postJobApi = async (action: ApiAction): Promise<unknown> =>
  await yemboApiCall({ method: 'post', url, ...action });
/**
 * `GET /job/:key`, answering the job itself. `action.data.key` names the job;
 * nothing else in `data` is sent (`GET /job` with query params is the job
 * search, now read through queries/Job `searchJobs`).
 */
export const getJobByKeyApi = async (action: ApiAction): Promise<unknown> =>
  await yemboApiCall({
    method: 'get',
    ...action,
    url: `${url}/${action.data?.key as string}`,
    data: {},
  });
export const putJobApi = async (action: ApiAction): Promise<unknown> => {
  try {
    // service-voyager-api exposes PUT /job/:key, not PUT /job with the key in
    // the body. Callers that pass their own `url` (generate-pdf and friends) keep
    // it; everything else gets the key appended.
    const keyInBody = action.data?.key as string | undefined;
    const resolvedUrl = action.url ?? (keyInBody ? `${url}/${keyInBody}` : url);
    return await yemboApiCall({ method: 'put', ...action, url: resolvedUrl });
  } finally {
    const jobKey = action.data.key as string | undefined;

    if (jobKey) {
      const invalidatePromises: Promise<void>[] = [
        queryClient.invalidateQueries({
          queryKey: [JOB_QUERY_KEY, ENTITY_KEY, jobKey],
        }),
        queryClient.invalidateQueries({
          queryKey: [ROOM_QUERY_KEY, LIST_KEY, jobKey],
        }),
      ];

      if (action.data.employeeKey) {
        const basicListQueryKeysToInvalidate = [ROOM_OPTION_LIST_QUERY_KEY];

        basicListQueryKeysToInvalidate.forEach((basicKey) =>
          invalidatePromises.push(
            queryClient.invalidateQueries({
              predicate: ({ queryKey }) => queryKey.includes(basicKey) && queryKey.includes(jobKey),
            })
          )
        );

        invalidatePromises.push(
          queryClient.invalidateQueries({
            queryKey: [EVENTS_QUERY_KEY, LIST_KEY],
          })
        );
      }

      await Promise.all(invalidatePromises);
    }
  }
};
export const deleteJobApi = async (action: ApiAction): Promise<unknown> =>
  await yemboApiCall({ method: 'delete', url, ...action });
const redactJobMediaAPI = async (action: ApiAction): Promise<unknown> =>
  await yemboApiCall({ method: 'put', url: `${url}/redact-media`, ...action });
const sendVisualInventoryApi = async (action: ApiAction): Promise<unknown> =>
  await yemboApiCall({
    method: 'post',
    url: `${url}/send-visual-inventory`,
    ...action,
  });

interface RedactJobMediaProps {
  jobKey: string;
  password: string;
  onSuccess: () => void;
}

export const redactJobMedia = async (props: RedactJobMediaProps): Promise<void> => {
  const { jobKey, password } = props;
  const response = (await redactJobMediaAPI({
    data: { key: jobKey, password },
  })) as { status: { type: string }[] };

  if (response.status[0].type === 'ok') {
    props.onSuccess();
    notify.success('All media files associated with this job as successfully been redacted.');
  }
};

interface SendVisualInventoryProps {
  jobKey: string;
  version: number;
  emails: string[];
  onError?: (error: unknown) => void;
  onSuccess?: () => void;
}

export const sendVisualInventory = async (props: SendVisualInventoryProps): Promise<void> => {
  const { jobKey, version, emails, onError, onSuccess } = props;

  const response = await sendVisualInventoryApi({
    data: { key: jobKey, version, emails },
    onError,
    onSuccess,
  });

  if (!response) {
    throw Error('Sending Visual Inventory to your selected contacts failed');
  }
};

export const createSampleJobApi = async (action: ApiAction): Promise<unknown> =>
  await postJobApi({ url: `${url}/sample`, ...action });

export const startPdfPoll = ({
  key,
  latestPdfVersion,
}: {
  key: string;
  latestPdfVersion: number;
}): Promise<unknown> => {
  return withShortPoll({
    apiCall: getJobByKeyApi,
    data: { key },
    didSucceed: (response: unknown) => ((response as Job).pdfs || []).some((pdf) => pdf.version > latestPdfVersion),
  });
};

interface GenerateJobPdfProps {
  data: {
    key: string;
    latestPdfVersion: number;
    [key: string]: unknown;
  };
}

export const generateJobPdfFlow = async (props: GenerateJobPdfProps): Promise<void> => {
  const { data } = props;
  const { key, latestPdfVersion } = data;

  const response = await putJobApi({ data, url: `${url}/generate-pdf` });

  if (response) {
    notify.success('Generating your Visual Inventory now. It should be available in a few seconds.');
    const onSuccess = () => {
      notify.success('Your PDF is ready! Check the Visual Inventory list to view or download.');
    };

    const pollResponse = await startPdfPoll({ key, latestPdfVersion });

    if ((pollResponse as Job | undefined)?.key) onSuccess();
    else notify.error('Sorry, we encountered an error. Please try again in a bit.');
  }
};

interface GenerateCubeSheetProps {
  data: {
    key: string;
    latestCubeSheetVersion: number;
    [key: string]: unknown;
  };
  onSuccess: () => void;
}

export const generateCubeSheetFlow = async (props: GenerateCubeSheetProps): Promise<void> => {
  const { data } = props;
  const { key, latestCubeSheetVersion } = data;
  const response = await putJobApi({ data, url: `${url}/generate-xlsx` });

  if (response) {
    notify.success(`Generating your cube sheet now. It should be available in a few seconds.`);
    const onSuccess = () => {
      notify.success(
        "Your cube sheet is ready! Check the cube sheet list if your download doesn't start automatically."
      );

      props.onSuccess();
    };

    const pollResponse = await withShortPoll({
      apiCall: getJobByKeyApi,
      data: { key },
      didSucceed: (response: unknown) =>
        ((response as Job).cubeSheets || []).some((cubeSheet) => cubeSheet.version > latestCubeSheetVersion),
    });

    if ((pollResponse as Job | undefined)?.key) onSuccess();
    else notify.error('Sorry, we encountered an error. Please try again in a bit.');
  }
};

export const putJobApiPlaceKeys = async (action: ApiAction, stopKeys: string[]): Promise<unknown> => {
  const { placeKey, jobKey, placeType: type } = action.data;
  const data: Record<string, unknown> = { key: jobKey };

  const placeType = type || placeTypes.find((placeType) => action.type.includes(placeType));
  const isPost = action.type.includes('_post_');

  switch (placeType) {
    case 'origin':
      data.originKey = isPost ? placeKey : null;
      break;
    case 'destination':
      data.destinationKey = isPost ? placeKey : null;
      break;
    case 'stop':
      {
        if (isPost) {
          data.stopKeys = [...stopKeys, placeKey];
        } else {
          data.stopKeys = stopKeys.filter((stopKey) => stopKey !== placeKey);
        }
      }
      break;
    default:
      break;
  }

  return await putJobApi({ data, skipDispatch: action.skipDispatch });
};

interface CreateBlankSurveyData {
  origin: { beds?: string | number | null; [key: string]: unknown };
  destination: Record<string, unknown>;
  company: { key: string };
  employee: { key: string };
  insured: Record<string, unknown>;
  job: Record<string, unknown>;
}

export const createBlankSurveyFlow = async ({ data }: { data: CreateBlankSurveyData }): Promise<{ job: Job }> => {
  const {
    origin,
    destination,
    company: { key: companyKey },
    employee: { key: employeeKey },
  } = data;

  // POST /insured and POST /job both answer the record itself.
  const insured = await postInsured({
    ...data.insured,
    companyKey,
    shouldNotify: false,
  } as unknown as Parameters<typeof postInsured>[0]); // dont send insured unassigned notification since insured cant be unassigned if created from the UI
  const job = (await postJobApi({
    data: {
      ...data.job,
      insuredKey: insured.key,
      employeeKey,
      companyKey,
      isManual: true,
      origin,
      destination,
    },
  })) as Job;

  await initializeRooms({
    beds: origin.beds ? Number(origin.beds) : null,
    jobKey: job.key,
  });

  return { job };
};

interface UpdateJobSurveyTypesProps {
  job: Pick<Job, 'key' | 'surveyTypes' | 'status'>;
  shouldFetchJob?: boolean;
  surveyType: SurveyType;
}

export const updateJobSurveyTypes = async ({
  job,
  shouldFetchJob,
  surveyType,
}: UpdateJobSurveyTypesProps): Promise<void> => {
  let updatedJob = job;

  if (shouldFetchJob) {
    try {
      updatedJob = (await getJobByKeyApi({
        data: { key: job.key },
        skipDispatch: true,
      })) as Job;
    } catch {
      updatedJob = job;
    }
  }

  const surveyTypesWithoutManual = updatedJob.surveyTypes.filter((type) => type !== 'manual');

  await putJobApi({
    data: {
      key: updatedJob.key,
      status: updatedJob.status,
      surveyTypes: (surveyTypesWithoutManual as SurveyType[]).includes(surveyType)
        ? surveyTypesWithoutManual
        : [...surveyTypesWithoutManual, surveyType],
    },
  });
};

interface MarkJobReviewedProps {
  authUser: Employee;
  jobKey: string;
  putEmployee: (employee: Partial<Employee> & { key: string }) => Promise<unknown>;
}

export const markJobReviewed = async ({ authUser, jobKey, putEmployee }: MarkJobReviewedProps): Promise<void> => {
  const { key: authUserKey, firstReviewedAt } = authUser;

  try {
    const jobResponse = (await putJobApi({
      data: {
        key: jobKey,
        status: 'reviewed',
        reviewedBy: authUserKey,
        reviewedAt: new Date().toISOString(),
      },
    })) as { key?: string };

    if (jobResponse.key && !firstReviewedAt) {
      await putEmployee({
        key: authUserKey,
        firstReviewedAt: dayjs().format('YYYY-MM-DDTHH:mm:ss.SSSZ'),
      });
    }
  } catch {
    notify.error('There was an error marking the job as reviewed. Please try again later.');
  }
};
